import { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { track, trackCta } from '../../lib/analytics';

type Athlete = {
  id: number;
  name: string;
  seedText: string;
  seedSeconds: number | null;
  originalLine: string;
};

type HeatAssignment = Athlete & {
  lane: number;
  seedRank: number;
};

type SeededHeat = {
  heatNumber: number;
  assignments: HeatAssignment[];
};

const sampleEntries = `Ava Johnson, 12.42
Mia Chen, 12.55
Sofia Patel, 12.70
Grace Williams, 12.83
Emma Rodriguez, 13.01
Harper Smith, 13.12
Nora Davis, 13.29
Lily Brown, 13.44
Zoey Miller, 13.68
Ella Wilson, NT`;

const laneOptions = [4, 5, 6, 7, 8, 9];

const pageTitle = 'Free track and field heat seeder - Baton';
const pageDescription =
  'Seed track and field heats online for free. Paste athletes and seed times, choose lane count, and generate snake-seeded heats with middle-out lane assignments.';

export default function HeatSeederPage() {
  const [entries, setEntries] = useState(sampleEntries);
  const [lanes, setLanes] = useState(8);
  const [eventName, setEventName] = useState('100m Dash');

  useEffect(() => {
    document.title = pageTitle;
    setMetaTag('name', 'description', pageDescription);
    setMetaTag('name', 'keywords', 'track and field heat seeder, seed heats, lane assignments, track meet software, heat sheets');
    setMetaTag('property', 'og:title', pageTitle);
    setMetaTag('property', 'og:description', pageDescription);
    setMetaTag('property', 'og:type', 'website');
    setMetaTag('property', 'og:url', window.location.href);
    setMetaTag('name', 'twitter:card', 'summary');
    setMetaTag('name', 'twitter:title', pageTitle);
    setMetaTag('name', 'twitter:description', pageDescription);
    setCanonicalUrl(window.location.href);
    setStructuredData({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Baton Heat Seeder',
      applicationCategory: 'SportsApplication',
      operatingSystem: 'Any',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
      description: pageDescription,
      featureList: [
        'Snake seeded track and field heats',
        'Middle-out lane assignments',
        'Supports timed and untimed entries',
        'Printable heat sheets',
      ],
    });
    window.scrollTo({ top: 0 });
  }, []);

  const parsed = useMemo(() => parseEntries(entries), [entries]);
  const seededHeats = useMemo(() => seedHeats(parsed.athletes, lanes), [parsed.athletes, lanes]);

  // Fire "Heat Seeder Used" once per page-load when the user has
  // edited entries away from the seed and produced at least one valid heat.
  // This filters out passive page views and only counts real tool usage.
  const usedRef = useRef(false);
  useEffect(() => {
    if (usedRef.current) return;
    if (entries === sampleEntries) return;
    if (parsed.athletes.length === 0 || seededHeats.length === 0) return;
    usedRef.current = true;
    track('Heat Seeder Used', {
      athletes: parsed.athletes.length,
      heats: seededHeats.length,
      lanes,
    });
  }, [entries, parsed.athletes.length, seededHeats.length, lanes]);

  return (
    <Box>
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="flex-start">
          <Grid item xs={12} md={5}>
            <Chip
              label="Free tool"
              size="small"
              sx={{ bgcolor: '#fef7ec', color: '#92400e', fontWeight: 600 }}
            />
            <Typography variant="h3" component="h1" sx={{ mt: 2, fontWeight: 800, letterSpacing: '-0.02em' }}>
              Free track and field heat seeder
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1.5, maxWidth: 620 }}>
              Paste athletes and seed times, choose your lane count, and generate clean
              snake-seeded heats with middle-out lane assignments. No signup, no email gate.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
              <Button
                variant="contained"
                onClick={() => document.getElementById('heat-seeder-tool')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                aria-label="Start seeding heats"
              >
                Seed heats now
              </Button>
              <Button
                component={RouterLink}
                to="/#waitlist"
                variant="outlined"
                aria-label="Get Baton early access"
                onClick={() => trackCta('Waitlist', 'heat_seeder')}
              >
                Get Baton early access
              </Button>
            </Stack>

            <Card variant="outlined" sx={{ mt: 4, borderRadius: 3, bgcolor: '#eff6fc', borderColor: '#a8c8e3' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
                  How the heat seeder works
                </Typography>
                <Stack spacing={1.25} sx={{ mt: 2 }}>
                  {[
                    'Timed entries are sorted fastest to slowest. NT entries are placed after timed entries.',
                    'Athletes are distributed across heats using a snake pattern to keep heats balanced.',
                    'Within each heat, the fastest athlete gets the most central lane, then lanes fill outward.',
                    'Use the print button to create a coach-friendly heat sheet for meet day.',
                  ].map((line) => (
                    <Stack key={line} direction="row" spacing={1.25}>
                      <Box sx={{ mt: 0.85, width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />
                      <Typography variant="body2" color="text.secondary">{line}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            <Card id="heat-seeder-tool" variant="outlined" sx={{ borderRadius: 3, scrollMarginTop: 96 }}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Stack spacing={2.5}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}>
                      <TextField
                        label="Event name"
                        value={eventName}
                        onChange={(event) => setEventName(event.target.value)}
                        inputProps={{ 'aria-label': 'Event name' }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        select
                        label="Lanes"
                        value={lanes}
                        onChange={(event) => setLanes(Number(event.target.value))}
                        inputProps={{ 'aria-label': 'Number of lanes' }}
                      >
                        {laneOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option} lanes
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>

                  <TextField
                    label="Athletes and seed times"
                    value={entries}
                    onChange={(event) => setEntries(event.target.value)}
                    multiline
                    minRows={10}
                    helperText="One athlete per line. Use formats like 'Ava Johnson, 12.42', 'Ava Johnson 12.42', 'Ava Johnson, 1:05.20', or 'Ava Johnson, NT'."
                    inputProps={{ 'aria-label': 'Athletes and seed times' }}
                  />

                  {parsed.errors.length > 0 && (
                    <Alert severity="warning">
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Some lines need attention
                      </Typography>
                      <Box component="ul" sx={{ my: 0.5, pl: 2.5 }}>
                        {parsed.errors.map((error) => (
                          <li key={error}>{error}</li>
                        ))}
                      </Box>
                    </Alert>
                  )}

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Button
                      variant="contained"
                      onClick={() => window.print()}
                      disabled={seededHeats.length === 0}
                      aria-label="Print seeded heat sheet"
                    >
                      Print heat sheet
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setEntries(sampleEntries)}
                      aria-label="Load sample entries"
                    >
                      Load sample
                    </Button>
                    <Button
                      variant="text"
                      color="inherit"
                      onClick={() => setEntries('')}
                      aria-label="Clear athlete entries"
                    >
                      Clear
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <Box sx={{ bgcolor: 'background.paper', py: { xs: 5, md: 7 }, '@media print': { py: 0 } }}>
        <Container maxWidth="lg">
          <Box sx={{ '@media print': { display: 'none' } }}>
            <Typography variant="h4" component="h2" sx={{ fontWeight: 700 }}>
              Seeded heat sheet
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              {parsed.athletes.length > 0
                ? `${parsed.athletes.length} athletes across ${seededHeats.length} heat${seededHeats.length === 1 ? '' : 's'}`
                : 'Add athletes above to generate heat assignments.'}
            </Typography>
          </Box>

          <Box
            sx={{
              mt: { xs: 3, md: 4 },
              '@media print': {
                mt: 0,
                '& .no-print': { display: 'none' },
              },
            }}
          >
            <PrintHeader eventName={eventName} lanes={lanes} heatCount={seededHeats.length} athleteCount={parsed.athletes.length} />
            {seededHeats.length > 0 ? (
              <Grid container spacing={2.5}>
                {seededHeats.map((heat) => (
                  <Grid item xs={12} md={6} key={heat.heatNumber}>
                    <HeatCard heat={heat} />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Card variant="outlined" sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    Paste athlete entries to generate lane assignments.
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 6, md: 9 }, '@media print': { display: 'none' } }}>
        <Typography variant="h4" component="h2" sx={{ fontWeight: 700 }}>
          Track heat seeding FAQ
        </Typography>
        <Stack spacing={3} sx={{ mt: 3 }}>
          {[
            {
              q: 'What does snake seeding mean?',
              a: 'Snake seeding distributes ranked athletes across heats in alternating directions, which helps keep each heat competitive instead of stacking one heat with all the fastest athletes.',
            },
            {
              q: 'How are lanes assigned?',
              a: 'Each heat is assigned lanes from the middle outward. On an 8-lane track, the lane order is 4, 5, 3, 6, 2, 7, 1, 8.',
            },
            {
              q: 'Can I use NT entries?',
              a: 'Yes. Enter NT, N/T, or no time. Untimed athletes are seeded after timed athletes and still receive lanes.',
            },
            {
              q: 'Is this a replacement for meet management software?',
              a: 'No. This is a quick free utility for coaches who need fast heat sheets. Baton also provides roster, meet, registration, and parent communication tools.',
            },
          ].map((item) => (
            <Box key={item.q}>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 700 }}>
                {item.q}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                {item.a}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Container>
    </Box>
  );
}

function HeatCard({ heat }: { heat: SeededHeat }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: '100%', breakInside: 'avoid' }}>
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ px: 2.25, py: 1.75, bgcolor: '#0b5394', color: '#fff', borderRadius: '12px 12px 0 0' }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Heat {heat.heatNumber}
          </Typography>
        </Box>
        <Box>
          {heat.assignments.map((assignment, index) => (
            <Box key={assignment.id}>
              <Grid container alignItems="center" spacing={1.5} sx={{ px: 2.25, py: 1.4 }}>
                <Grid item xs={2} sm={1.6}>
                  <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                    Lane
                  </Typography>
                  <Typography sx={{ fontWeight: 800 }}>{assignment.lane}</Typography>
                </Grid>
                <Grid item xs={6.5} sm={7.4}>
                  <Typography sx={{ fontWeight: 700 }}>{assignment.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Seed #{assignment.seedRank}
                  </Typography>
                </Grid>
                <Grid item xs={3.5} sm={3} sx={{ textAlign: 'right' }}>
                  <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                    Mark
                  </Typography>
                  <Typography sx={{ fontWeight: 700 }}>{assignment.seedText}</Typography>
                </Grid>
              </Grid>
              {index < heat.assignments.length - 1 && <Divider />}
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

function PrintHeader({
  eventName,
  lanes,
  heatCount,
  athleteCount,
}: {
  eventName: string;
  lanes: number;
  heatCount: number;
  athleteCount: number;
}) {
  if (athleteCount === 0) return null;

  return (
    <Box sx={{ display: 'none', '@media print': { display: 'block', mb: 2 } }}>
      <Typography variant="h4" sx={{ fontWeight: 800 }}>
        {eventName || 'Seeded Heat Sheet'}
      </Typography>
      <Typography color="text.secondary">
        {athleteCount} athletes · {heatCount} heat{heatCount === 1 ? '' : 's'} · {lanes} lanes
      </Typography>
    </Box>
  );
}

function parseEntries(input: string): { athletes: Athlete[]; errors: string[] } {
  const athletes: Athlete[] = [];
  const errors: string[] = [];

  input
    .split(/\r?\n/)
    .map((line) => line.trim())
    .forEach((line, index) => {
      if (!line) return;

      const parsed = parseEntryLine(line);
      if (!parsed) {
        errors.push(`Line ${index + 1}: add a name and seed time, such as "Ava Johnson, 12.42" or "Ava Johnson, NT".`);
        return;
      }

      athletes.push({
        id: index,
        name: parsed.name,
        seedText: parsed.seedText,
        seedSeconds: parsed.seedSeconds,
        originalLine: line,
      });
    });

  return { athletes, errors };
}

function parseEntryLine(line: string): Omit<Athlete, 'id' | 'originalLine'> | null {
  const commaParts = line.split(',').map((part) => part.trim()).filter(Boolean);
  const candidate = commaParts.length >= 2
    ? { name: commaParts.slice(0, -1).join(', '), seed: commaParts[commaParts.length - 1] }
    : splitLineOnTrailingSeed(line);

  if (!candidate?.name || !candidate.seed) return null;

  const seedSeconds = parseSeedTime(candidate.seed);
  if (seedSeconds === undefined) return null;

  return {
    name: candidate.name,
    seedText: seedSeconds === null ? 'NT' : candidate.seed.trim(),
    seedSeconds,
  };
}

function splitLineOnTrailingSeed(line: string): { name: string; seed: string } | null {
  const match = line.match(/^(.+?)\s+((?:\d+:)?\d{1,2}(?:\.\d+)?|NT|N\/T|no time)$/i);
  if (!match) return null;

  return {
    name: match[1].trim(),
    seed: match[2].trim(),
  };
}

function parseSeedTime(seed: string): number | null | undefined {
  const normalized = seed.trim().toLowerCase();
  if (['nt', 'n/t', 'no time'].includes(normalized)) return null;

  const time = normalized.replace(/s$/, '');
  if (time.includes(':')) {
    const parts = time.split(':').map(Number);
    if (parts.length !== 2 || parts.some((part) => Number.isNaN(part))) return undefined;
    return parts[0] * 60 + parts[1];
  }

  const seconds = Number(time);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : undefined;
}

function seedHeats(athletes: Athlete[], lanes: number): SeededHeat[] {
  if (athletes.length === 0) return [];

  const rankedAthletes = [...athletes].sort((a, b) => {
    if (a.seedSeconds === null && b.seedSeconds === null) return a.id - b.id;
    if (a.seedSeconds === null) return 1;
    if (b.seedSeconds === null) return -1;
    return a.seedSeconds - b.seedSeconds || a.id - b.id;
  });

  const heatCount = Math.ceil(rankedAthletes.length / lanes);
  const laneOrder = getMiddleOutLaneOrder(lanes);
  const heats: SeededHeat[] = Array.from({ length: heatCount }, (_, index) => ({
    heatNumber: index + 1,
    assignments: [],
  }));

  rankedAthletes.forEach((athlete, index) => {
    const heatIndex = getSnakeHeatIndex(index, heatCount);
    const heat = heats[heatIndex];
    const lane = laneOrder[heat.assignments.length] ?? heat.assignments.length + 1;

    heat.assignments.push({
      ...athlete,
      lane,
      seedRank: index + 1,
    });
  });

  return heats;
}

function getSnakeHeatIndex(index: number, heatCount: number) {
  if (heatCount <= 1) return 0;

  const cycleLength = heatCount * 2;
  const position = index % cycleLength;
  if (position < heatCount) return position;

  return cycleLength - position - 1;
}

function getMiddleOutLaneOrder(lanes: number) {
  const center = (lanes + 1) / 2;
  return Array.from({ length: lanes }, (_, index) => index + 1).sort((a, b) => {
    const distance = Math.abs(a - center) - Math.abs(b - center);
    return distance || a - b;
  });
}

function setMetaTag(attribute: 'name' | 'property', key: string, content: string) {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.content = content;
}

function setCanonicalUrl(url: string) {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = url;
}

function setStructuredData(data: Record<string, unknown>) {
  const id = 'heat-seeder-structured-data';
  let script = document.getElementById(id) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}
